import Dialog from "../../General/ConfirmableDialog";
import {
  useState,
  useEffect } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { formatRentValues } from "../../../helpers/formatingFunctions";

interface RentDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  assetId: string;
  assetType: "truck" | "trailer";
  startDate: string;
  endDate: string;
  token: string | null | undefined;
}

const RentDetailsPopup = ({
  open,
  onClose,
  companyId,
  assetId,
  assetType,
  startDate,
  endDate,
  token,
}: RentDetailsPopupProps) => {
  const [rentDetails, setRentDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRentDetails = async () => {
      if (!open) return;

      setIsLoading(true);
      setError(null);

      try {
        const { data } = await axios.get(`${apiVariable}/getMatchRent`, {
          params: {
            companyId,
            truckId: assetType === "truck" ? assetId : null,
            trailerId: assetType === "trailer" ? assetId : null,
            start: startDate,
            end: endDate,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setRentDetails(data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError("No matching rent record found for this period");
        } else {
          setError(err.response?.data || "Failed to fetch rent details");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchRentDetails();
  }, [open, companyId, assetId, assetType, startDate, endDate, token]);

  const renderRentDetails = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography color="error" variant="h6" align="center">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!rentDetails) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" align="center">
            No rent details available
          </Typography>
        </Box>
      );
    }

    const fields = [
      { key: "unitNumber", label: "Unit Number" },
      { key: "authorityCompanyName", label: "Authority Company" },
      { key: "billToCompanyName", label: "Bill To" },
      { key: "driverFullName", label: "Driver" },
      { key: "rentalTerm", label: "Rental Term" },
      { key: "billingFrequency", label: "Billing Frequency" },
      { key: "baseRentalRate", label: "Base Rate" },
      { key: "mileageRate", label: "Mileage Rate" },
      { key: "leaseTerm", label: "Lease Term" },
      { key: "startDate", label: "Start Date" },
      { key: "endDate", label: "End Date" },
      { key: "downPayment", label: "Reimbursable deposit" },
      { key: "mileageCaps", label: "Mileage Caps" },
      { key: "startMileage", label: "Start Mileage" },
      { key: "endMileage", label: "End Mileage" },
      { key: "remainingBalance", label: "Remaining Balance" },
    ];

    return (
      <Grid container spacing={2}>
        {fields.map(({ key, label }) => (
          <Grid item xs={6} key={key}>
            <Typography variant="subtitle2" color="textSecondary">
              {label}
            </Typography>
            <Typography>
              {formatRentValues(
                key,
                rentDetails[key],
                rentDetails.billingFrequency
              )}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rent Details</DialogTitle>
      <DialogContent>{renderRentDetails()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RentDetailsPopup;
